import {ActionList, type ActionListProps, ActionMenu, type ActionMenuProps, type TouchOrMouseEvent} from '@primer/react'
import {type ComponentProps, type KeyboardEventHandler, type ReactElement, useCallback} from 'react'
import {useNestableActionMenu} from './NestableActionMenuContext'

type RootMenuProps = {
  anchor: ReactElement
  isNested: boolean
  closeStack: () => void
  open: boolean
  overlayProps?: Partial<ComponentProps<(typeof ActionMenu)['Overlay']>>
  actionMenuProps?: Partial<ActionMenuProps>
  actionListProps?: Partial<ActionListProps>
  externalOverlayOnKeyDown: KeyboardEventHandler<HTMLDivElement> | undefined
  setOpen: (newOpen: boolean) => void
  children: ReactElement | ReactElement[]
}

export const RootMenu = ({
  anchor,
  children,
  externalOverlayOnKeyDown,
  isNested,
  open,
  closeStack,
  setOpen,
  overlayProps,
  actionListProps,
  actionMenuProps,
}: RootMenuProps) => {
  const context = useNestableActionMenu()
  const anyNestedSelectPanelsOpen = context?.anyNestedSelectPanelsOpen ?? false

  // We want to close the entire stack _only_ when a menu item was clicked; otherwise close only the top menu.
  // However, there's no way to tell when an item was clicked because there's no `ActionList.onSelectItem` event and
  // we don't have access to all the items to listen to their `onSelect` handlers. So the only way is to handle all
  // possible _other_ events explicitly so that the only thing possibly causing `onOpenChange` to get called is an
  // item selection.

  const overlayOnKeyDown = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    event => {
      externalOverlayOnKeyDown?.(event)

      // When a nested SelectPanel is open, stop escape key press from closing parent menu, too; only that nested
      // SelectPanel should close.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Escape' && anyNestedSelectPanelsOpen) return

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (!event.defaultPrevented && (event.key === 'Escape' || (isNested && event.key === 'ArrowLeft'))) {
        setOpen(false)
        event.stopPropagation()
      }
    },
    [externalOverlayOnKeyDown, isNested, anyNestedSelectPanelsOpen, setOpen],
  )

  const overlayOnClickOutside = (event: TouchOrMouseEvent) => {
    overlayProps?.onClickOutside?.(event)
    if (!event.defaultPrevented) setOpen(false)
  }

  // The one thing we can't handle explicitly that we'd like to is an anchor click. Clicking the anchor should only
  // close the top-level menu, but there's no way to distinguish an anchor click from an item click. Cloning the
  // anchor to add an `onClick` handler doesn't work because `ActionMenu` also clones and overrides that handler.

  // Now all other close events close the entire stack
  const menuOnOpenChange = (newOpen: boolean) => (newOpen ? setOpen(true) : closeStack())

  return (
    <ActionMenu {...actionMenuProps} open={open} onOpenChange={menuOnOpenChange}>
      <ActionMenu.Anchor>{anchor}</ActionMenu.Anchor>

      <ActionMenu.Overlay
        align="start"
        side={isNested ? 'outside-right' : 'outside-bottom'}
        {...overlayProps}
        onKeyDown={overlayOnKeyDown}
        onClickOutside={overlayOnClickOutside}
      >
        <ActionList {...actionListProps}>{children}</ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

try{ RootMenu.displayName ||= 'RootMenu' } catch {}